import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, DIRECTION_OF, RESOURCES, SUPPORTED_LOCALES } from './locales';
import type { Direction, I18nContextValue, Locale } from './types';

const STORAGE_KEY = 'solara.locale';

let initialized = false;

function ensureI18nInitialized(initialLocale: Locale) {
  if (initialized) return;
  initialized = true;
  void i18next.use(initReactI18next).init({
    resources: RESOURCES,
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    ns: [
      'common',
      'errors',
      'onboarding',
      'invoice',
      'invoiceDetail',
      'inbox',
      'approvals',
      'archive',
      'compliance',
      'templates',
      'settings',
      'notifications',
      'dashboard',
      'admin',
      'tickstar',
      'glossary',
      'login',
      'p1',
    ],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

function readPersistedLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as string[]).includes(stored)) {
    return stored as Locale;
  }
  const browser = window.navigator.language.toLowerCase();
  if (browser.startsWith('ar')) return 'ar-AE';
  if (browser.startsWith('hu')) return 'hu-HU';
  return DEFAULT_LOCALE;
}

function applyDocumentDirection(locale: Locale, direction: Direction) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', locale);
  root.setAttribute('dir', direction);
  root.dataset.locale = locale;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readPersistedLocale());
  ensureI18nInitialized(locale);

  const direction = DIRECTION_OF[locale];

  useEffect(() => {
    applyDocumentDirection(locale, direction);
    void i18next.changeLanguage(locale);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }
  }, [locale, direction]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const toggleLocale = useCallback(() => {
    // Cycle through supported locales: en-AE → ar-AE → hu-HU → en-AE
    setLocaleState((current) => {
      const idx = SUPPORTED_LOCALES.indexOf(current);
      return SUPPORTED_LOCALES[(idx + 1) % SUPPORTED_LOCALES.length] ?? DEFAULT_LOCALE;
    });
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      direction,
      setLocale,
      toggleLocale,
      t: (key, vars) => i18next.t(key, vars ?? {}),
    }),
    [locale, direction, setLocale, toggleLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return ctx;
}

export function useDirection(): Direction {
  return useI18n().direction;
}

// Re-export react-i18next's useTranslation so consumers can use either API.
export { useTranslation };
